/**
 * Correcting an employee record — owned by this feature, implemented in
 * `infrastructure/` (§3.4, §4.1).
 *
 * Separate from `ProgressPort`, which is read-only on purpose and says so. The
 * committee reads progress far more often than it corrects a record, and a
 * port that can only read cannot grow a write by accident.
 */

/**
 * What the committee may correct.
 *
 * Only what HR is fixing when the roster is wrong. Enrolment, coverage, and
 * anything the employee fills in themselves are absent: this is for a
 * misspelt name or a branch recorded against the wrong campus, not for
 * deciding what someone is entitled to.
 */
export interface EmployeeCorrection {
  readonly fullName: string;
  readonly branchId: string | null;
  /**
   * The school address this record is matched by.
   *
   * The one field on this form that changes who can reach the record: an
   * employee signing in with a workspace address is linked to whichever record
   * carries it. The first live test found the roster holding `anas.m@` for
   * someone whose account is `anas@`, and nothing could be done about it —
   * which is why this is editable.
   *
   * Changing it does not unlink an account already linked; it decides who may
   * link next.
   */
  readonly workEmail: string | null;
  readonly phone: string | null;
  readonly address: string | null;
  /** Cross-checks, not facts: what the roster expects to see entered. */
  readonly declaredChildrenCount: number;
  readonly declaredMaritalStatus: boolean | null;
}

export type AdminFailureCode =
  | 'forbidden'
  | 'invalid'
  | 'not_found'
  | 'email_taken'
  | 'network'
  | 'unknown';

export class EmployeeAdminError extends Error {
  readonly code: AdminFailureCode;

  constructor(code: AdminFailureCode, message: string) {
    super(message);
    this.name = 'EmployeeAdminError';
    this.code = code;
  }
}

export interface EmployeeAdminPort {
  /**
   * The record as it stands, for filling the correction form.
   *
   * Read when the form opens rather than carried on every progress row: phone
   * and address are needed by one editor, not by a list of 872 (§11.5.1).
   */
  getEmployee(employeeId: string): Promise<EmployeeCorrection | null>;

  /**
   * Applies a correction.
   *
   * `updatedBy` is not accepted from the caller: the rules require it to equal
   * the caller's own employee id, and who changed a record is not a field a
   * screen gets to assert (§4.2).
   */
  correctEmployee(employeeId: string, correction: EmployeeCorrection): Promise<void>;
}
